/** Allocation board with scheduler-owned commands and calendar presentation. */
import { RightsService } from '@jms/utils/util.permissions';
import { schedulerManageAppoitmentRights, ActivityEntityName, Allocation, EntityCalendarResourceType, EntityCalendarView, EntityViewDisplay } from '@jms/model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { allocationResourceAvailability } from './services/allocation.calendar.availability';
import { mapAllocationCalendarEvent } from './services/allocation.calendar.event';
import { loadAllocationCalendarResources } from './services/allocation.calendar.resources';

export const allocationManifest = registerManifest<Allocation>({
  entity: ActivityEntityName.Allocation,
  slug: 'allocation',
  operations: {
    edit: {
      kind: 'custom',
      load: () => import('./components/allocation.calendar.operation.component').then(module => module.AllocationCalendarOperationComponent),
      presentation: { mode: 'dialog', title: 'Edit allocation', width: '1100px', height: '760px', maxHeight: '100%' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(...schedulerManageAppoitmentRights),
    },
    create: {
      kind: 'custom',
      load: () => import('./components/allocation.calendar.operation.component').then(module => module.AllocationCalendarOperationComponent),
      presentation: { mode: 'dialog', title: 'New allocation', width: '1100px', height: '760px', maxHeight: '100%' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(...schedulerManageAppoitmentRights),
    },
  },
  presentation: {
    calendar: {
      event: { component: () => import('./components/allocation.calendar.event.component').then(module => module.AllocationCalendarEventComponent) },
      resource: { component: () => import('./components/allocation.calendar.resource.component').then(module => module.AllocationCalendarResourceComponent) },
    },
  },
  board: {
    supportedDisplays: [EntityViewDisplay.Table, EntityViewDisplay.List, EntityViewDisplay.Calendar],
    calendar: {
      // Every layout is available to the saved-view definition editor. Runtime boards render the
      // layout the selected view defines; they do not carry a second, conflicting layout switcher.
      supportedViews: Object.values(EntityCalendarView),
      defaultView: EntityCalendarView.ResourceTimelineWeek,
      resourceTypes: [EntityCalendarResourceType.Employee, EntityCalendarResourceType.Contractor, EntityCalendarResourceType.Unassigned],
      resourceSelection: { field: 'resource', entities: [ActivityEntityName.ContactEmployee, ActivityEntityName.Contractor], teamEntity: ActivityEntityName.Team },
      // A scheduler axis is the ROSTER, not the bookings: an employee with an empty week still has a
      // lane, and derivation (which only ever sees the allocations in range) cannot produce one. So
      // the roster is loaded from the employee and contractor contacts, with the Unassigned bucket
      // placed first.
      resourceLoader: loadAllocationCalendarResources,
      resourceAvailability: allocationResourceAvailability,
      eventMapper: mapAllocationCalendarEvent,
      actions: () => import('./services/allocation.calendar.actions').then(module => module.allocationCalendarActions),
    },
    defaults: {
      header: {
        title: 'Allocations',
        showSearch: true,
        showPaginator: false,
        showViewOptions: true,
      },
    },
  },
});
